import React, {
    useState,
    useEffect,
    useRef,
    ReactNode,
    ReactElement,
} from 'react';
import {DropdownControlled} from '@homeaway/react-dropdown';
import FilterGroupFooter from '../FilterGroupFooter/FilterGroupFooter';
import {Props as FilterGroupPillProps} from '../FilterGroupPill/FilterGroupPill';
import {MIN_RESULTS_TO_SEARCH} from '../../utils/constants';

import './FilterGroupDropdown.less';

export interface Props extends FilterGroupPillProps {
    filterPill: ReactElement;
    children: ReactNode;
    setIsGroupOpen: (isOpen: boolean) => void;
}

export default function FilterGroupDropdown({
    children,
    label,
    secondaryLabel,
    groupId,
    filters,
    results,
    selectedFilters,
    onDismiss,
    onDone,
    onClear,
    filterPill,
    setIsGroupOpen,
    customFiltersParams,
    isMobile,
    scrolling,
}: Props) {
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const FilterDropdownRef = useRef<HTMLDivElement>(null);
    const DROPDOWN_MENU_WIDTH = 408;

    useEffect(() => {
        if (scrolling) {
            setDropdownOpen(false);
        }
    }, [scrolling]);

    function offset(el: HTMLDivElement) {
        const rect = el.getBoundingClientRect();
        const scrollLeft =
            window.pageXOffset || document.documentElement.scrollLeft;

        return {
            left: rect.left,
            scrollLeft,
            right: rect.right + scrollLeft,
        };
    }

    // Returns true if dropdown should be aligned right
    function determineDropDownPosition() {
        const node = FilterDropdownRef.current;

        if (!isMobile && node) {
            const position = offset(node);
            const dropDownMenu = node.getElementsByClassName(
                'Dropdown__menu'
            )[0] as HTMLElement;

            if (position.left + DROPDOWN_MENU_WIDTH > window.innerWidth) {
                dropDownMenu.style.left = `${
                    position.right - DROPDOWN_MENU_WIDTH
                }px`;
                return true;
            }
            dropDownMenu.style.left = `${position.left}px`;
        }
        return false;
    }

    function handleDropdownClose(/* action = 'clicked outside' */) {
        setDropdownOpen(false);

        if (results >= MIN_RESULTS_TO_SEARCH) {
            onDone();
        } else {
            onDismiss();
        }

        // Disabled until this event SPEC is fixed
        // edapContext.trigger(FILTER_GROUP_HIDDEN, {
        //     filtergroupid: groupId,
        //     action_type: action,
        // });
    }

    function handleDropdownClick() {
        if (!isDropdownOpen) {
            setDropdownOpen(true);
        } else {
            handleDropdownClose();
        }
    }

    function handleOnBlurOrTap() {
        /* istanbul ignore else */
        if (isDropdownOpen) {
            handleDropdownClose();
        }
    }

    useEffect(() => {
        setIsGroupOpen(isDropdownOpen);
    }, [isDropdownOpen, setIsGroupOpen]);

    return (
        <div className="FilterGroupDropdown" ref={FilterDropdownRef}>
            <DropdownControlled
                id={groupId}
                open={isDropdownOpen}
                customToggle={React.cloneElement(filterPill, {
                    onClick: handleDropdownClick,
                })}
                label={label}
                onDropdownClick={handleDropdownClick}
                onBlur={handleOnBlurOrTap}
                onTouch={handleOnBlurOrTap}
                maxHeight={480}
                dropdownRight={determineDropDownPosition()}
            >
                <div className="FilterGroupDropdown__Container">
                    <span className="FilterGroupDropdown__Label">{label}</span>
                    {secondaryLabel && (
                        <span className="FilterGroupDropdown__SecondaryLabel">
                            {secondaryLabel}
                        </span>
                    )}
                    <div className="FilterGroupDropdown__Content">
                        {children}
                    </div>
                    <FilterGroupFooter
                        groupId={groupId}
                        customFiltersParams={customFiltersParams}
                        onDone={handleDropdownClose}
                        onClear={onClear}
                        results={results}
                        filters={filters}
                        selectedFilters={selectedFilters}
                    />
                </div>
            </DropdownControlled>
        </div>
    );
}

export {FilterGroupDropdown};
